/**
 * The settings overlay.
 *
 * Built from a list of rows rather than from two hard-coded sliders, because
 * the next things to land here — wave speed, input mode, graphics quality — are
 * the same shape, and a panel that has to be restructured to gain a row is a
 * panel nobody adds rows to.
 *
 * Every control is a native `<input type="range">`. That is not laziness: it
 * brings mouse drag, touch drag, arrow keys, Home/End, page up/down, focus
 * rings and screen-reader semantics for free, and a hand-rolled slider would
 * have to reimplement all of them badly before it did anything else.
 *
 * Changes apply on `input`, not `change`, so dragging is audible as it happens.
 * There is no Apply button — for a volume control, the preview *is* the setting.
 */

export interface SettingsSlider {
  readonly id: string;
  readonly label: string;
  /** Reads the current value, 0..1. Called when the panel opens. */
  readonly read: () => number;
  /** Applies a new value, 0..1. Called on every drag frame. */
  readonly write: (value: number) => void;
}

export interface SettingsAction {
  readonly id: string;
  readonly label: string;
  readonly run: () => void;
}

export interface SettingsPanelOptions {
  readonly parent?: HTMLElement;
  readonly sliders: readonly SettingsSlider[];
  readonly actions?: readonly SettingsAction[];
  /** Called after any slider moves, for persistence. */
  readonly onChange?: () => void;
}

const STYLE_ID = 'mandalizm-settings-style';

const CSS = `
.mandalizm-settings-toggle {
  position: fixed; right: 14px; bottom: 14px; z-index: 60;
  appearance: none; cursor: pointer;
  font: 11px/1 ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  letter-spacing: 0.18em; padding: 9px 14px; color: #bff4ee;
  background: rgba(0, 12, 18, 0.78); border: 1px solid rgba(90, 240, 220, 0.32);
  border-radius: 6px; transition: background 140ms ease, color 140ms ease;
}
.mandalizm-settings-toggle:hover { background: rgba(6, 62, 70, 0.92); color: #eafeff; }
.mandalizm-settings {
  position: fixed; right: 14px; bottom: 56px; z-index: 61; width: 300px;
  padding: 16px; box-sizing: border-box;
  font: 12px/1.6 ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  color: #cfeff2; background: rgba(0, 10, 16, 0.94);
  border: 1px solid rgba(80, 230, 210, 0.3); border-radius: 8px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.55);
}
.mandalizm-settings[hidden] { display: none; }
.mandalizm-settings__title {
  font-size: 11px; letter-spacing: 0.24em; color: rgba(150, 215, 225, 0.7);
  margin-bottom: 14px;
}
.mandalizm-settings__row { margin-bottom: 15px; }
.mandalizm-settings__head {
  display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 5px;
}
.mandalizm-settings__label { letter-spacing: 0.12em; color: #b8f2ee; }
.mandalizm-settings__value {
  font-variant-numeric: tabular-nums; color: #eafeff; min-width: 3.4em; text-align: right;
}
.mandalizm-settings__row input[type="range"] {
  width: 100%; margin: 0; accent-color: #16e0d0; cursor: pointer;
  /* A comfortable touch target without changing the control's own metrics. */
  padding: 6px 0; background: transparent;
}
.mandalizm-settings__actions { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 4px; }
.mandalizm-settings__action {
  appearance: none; cursor: pointer; font: inherit; font-size: 11px; letter-spacing: 0.12em;
  padding: 7px 12px; color: #b6e8e4;
  background: rgba(4, 42, 50, 0.85); border: 1px solid rgba(90, 240, 220, 0.28);
  border-radius: 5px;
}
.mandalizm-settings__action:hover { background: rgba(6, 76, 86, 0.95); color: #eafeff; }
`;

/** Internal 0..1 to the 0..100 the control and the read-out speak. */
export function toPercent(value: number): number {
  return Math.round(clamp01(value) * 100);
}

/** The 0..100 from the control back to the 0..1 the engine wants. */
export function fromPercent(percent: number): number {
  if (!Number.isFinite(percent)) return 0;
  return clamp01(percent / 100);
}

export function formatPercent(value: number): string {
  return `${toPercent(value)}%`;
}

export class SettingsPanel {
  readonly element: HTMLDivElement;
  readonly toggle: HTMLButtonElement;

  private readonly rows = new Map<string, { input: HTMLInputElement; value: HTMLSpanElement }>();
  private readonly sliders: readonly SettingsSlider[];
  private readonly onChange: (() => void) | undefined;

  constructor(options: SettingsPanelOptions) {
    injectStyle();
    const parent = options.parent ?? document.body;
    this.sliders = options.sliders;
    this.onChange = options.onChange;

    this.element = document.createElement('div');
    this.element.className = 'mandalizm-settings';
    this.element.hidden = true;
    this.element.setAttribute('role', 'group');
    this.element.setAttribute('aria-label', 'Settings');

    const title = document.createElement('div');
    title.className = 'mandalizm-settings__title';
    title.textContent = 'SETTINGS';
    this.element.appendChild(title);

    for (const slider of this.sliders) {
      this.element.appendChild(this.buildRow(slider));
    }

    if (options.actions !== undefined && options.actions.length > 0) {
      const actions = document.createElement('div');
      actions.className = 'mandalizm-settings__actions';
      for (const action of options.actions) {
        const button = document.createElement('button');
        button.className = 'mandalizm-settings__action';
        button.type = 'button';
        button.textContent = action.label;
        button.addEventListener('click', () => action.run());
        actions.appendChild(button);
      }
      this.element.appendChild(actions);
    }

    // Its own toggle, so the panel survives the start gate being dismissed and
    // stays reachable mid-song. Nothing here pauses the game — the volumes are
    // live, so adjusting them while playing is the point rather than a hazard.
    this.toggle = document.createElement('button');
    this.toggle.className = 'mandalizm-settings-toggle';
    this.toggle.type = 'button';
    this.toggle.textContent = 'SETTINGS';
    this.toggle.setAttribute('aria-expanded', 'false');
    this.toggle.addEventListener('click', () => this.setOpen(this.element.hidden));

    parent.append(this.element, this.toggle);
  }

  get open(): boolean {
    return !this.element.hidden;
  }

  /** Opens or closes, refreshing from the live values on the way in. */
  setOpen(open: boolean): void {
    if (open) this.refresh();
    this.element.hidden = !open;
    this.toggle.setAttribute('aria-expanded', String(open));
  }

  /** Re-reads every slider from its source. */
  refresh(): void {
    for (const slider of this.sliders) {
      const row = this.rows.get(slider.id);
      if (row === undefined) continue;
      const value = slider.read();
      row.input.value = String(toPercent(value));
      row.value.textContent = formatPercent(value);
    }
  }

  dispose(): void {
    this.element.remove();
    this.toggle.remove();
  }

  private buildRow(slider: SettingsSlider): HTMLDivElement {
    const row = document.createElement('div');
    row.className = 'mandalizm-settings__row';

    const head = document.createElement('div');
    head.className = 'mandalizm-settings__head';
    const label = document.createElement('label');
    label.className = 'mandalizm-settings__label';
    label.textContent = slider.label;
    label.htmlFor = `mandalizm-setting-${slider.id}`;
    const value = document.createElement('span');
    value.className = 'mandalizm-settings__value';
    head.append(label, value);

    const input = document.createElement('input');
    input.type = 'range';
    input.id = `mandalizm-setting-${slider.id}`;
    input.min = '0';
    input.max = '100';
    input.step = '1';
    input.value = String(toPercent(slider.read()));
    value.textContent = formatPercent(slider.read());
    // `input`, not `change`: the whole point is hearing the level while
    // dragging, and `change` only fires once the pointer is released.
    input.addEventListener('input', () => {
      const next = fromPercent(Number(input.value));
      slider.write(next);
      value.textContent = formatPercent(next);
      this.onChange?.();
    });

    row.append(head, input);
    this.rows.set(slider.id, { input, value });
    return row;
  }
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

function injectStyle(): void {
  if (document.getElementById(STYLE_ID) !== null) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = CSS;
  document.head.appendChild(style);
}
